import { Box, Typography, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useAlert } from "../functions/useAlert";
import { useSearchParams } from "react-router-dom";
import VersionHistoryDrawer from "../components/VersionHistoryDrawer";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import TabsList from "./TabsList";
import AgreementsPage from "./Agreementspage";
import FactFindOntologyPicker from "./FactFindOntologyPicker";
import ImportTemplateModal from "./ImportTemplateModal";
import TemplateListPanel from "./TemplateListPanel";
import TemplateEditorPanel from "./TemplateEditorPanel";
import TemplatePreviewPanel from "./TemplatePreviewPanel";
import { useDocTemplateManager } from "./hooks/useDocTemplateManager";
import { useVersionHistory } from "./hooks/useVersionHistory";
import { useResizableSplitPane } from "../hooks/useResizableSplitPane";

interface UploadDocumentTemplateProps {
  selectedOrganization?: string;
  organisationList?: { _id: string; name: string }[] | null;
  className?: string;
}

const UploadDocumentTemplate = ({
  selectedOrganization,
  className,
}: UploadDocumentTemplateProps) => {
  const [setAlert] = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templateListCollapsed, setTemplateListCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);

  const manager = useDocTemplateManager(selectedOrganization, activeTab, setActiveTab);
  const { editorWidth, isDragging, containerRef, handleMouseDown } =
    useResizableSplitPane(templateListCollapsed);
  const versionHistory = useVersionHistory({
    list: manager.list,
    selectedIndex: manager.selectedIndex,
    isGlobalAdmin: manager.isGlobalAdmin,
    templateData: manager.templateData,
    setAlert,
    setSelectedIndex: manager.setSelectedIndex,
    setPowerPointFile: manager.setPowerPointFile,
    setMonacoContent: manager.setMonacoContent,
    setTemplateSections: manager.setTemplateSections,
    setEditorMode: manager.setEditorMode,
    setSectionsDirty: manager.setSectionsDirty,
    setTemplateData: manager.setTemplateData,
    sectionsParseSourceRef: manager.sectionsParseSourceRef,
  });

  // Prevent collapse on mobile devices
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1200 && templateListCollapsed) {
        // lg breakpoint
        setTemplateListCollapsed(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Check on component mount

    return () => window.removeEventListener("resize", handleResize);
  }, [templateListCollapsed]);

  // ✅ Handle URL parameters to load and select a specific template
  // ✅ Auto-select the first template if templates exist and none is selected
  useEffect(() => {
    const templateIdFromUrl = searchParams.get("templateId");

    if (templateIdFromUrl && manager.list.length > 0) {
      // Find the template in the list
      const templateIndex = manager.list.findIndex(
        (tpl) => tpl.templateId === templateIdFromUrl,
      );

      if (templateIndex !== -1) {
        // Select the template
        manager.handleTemplateSelect(templateIndex);

        // Clear URL parameters after loading
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("templateId");
        newSearchParams.delete("orgId");
        setSearchParams(newSearchParams, { replace: true });
      }
    } else if (
      manager.list.length > 0 &&
      manager.selectedIndex === null &&
      !templateIdFromUrl
    ) {
      // Auto-select the first template of the current active tab type
      const tabTypeMap = {
        0: "file note",
        1: "fact find",
        2: "powerpoint",
        5: "email",
        6: "skill",
      };
      const targetType = tabTypeMap[activeTab];

      // Get all templates matching the current tab type
      const matchingTemplates = manager.list
        .map((tpl, idx) => ({ tpl, idx }))
        .filter(({ tpl }) => tpl.templateType === targetType);

      // Special handling for PowerPoint: auto-select if only one template exists for the org
      if (targetType === "powerpoint" && matchingTemplates.length === 1) {
        manager.handleTemplateSelect(matchingTemplates[0].idx);
      } else if (matchingTemplates.length > 0) {
        // For other template types or multiple PowerPoints, select the first matching one
        manager.handleTemplateSelect(matchingTemplates[0].idx);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manager.list, searchParams]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    manager.resetForTabChange(newValue);
  };

  if (manager.loading) {
    return <CircularProgress />;
  }

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={{ xs: 2, lg: 3 }}
        mt={2}
        marginLeft={{ xs: 1, lg: 3 }}
        marginRight={{ xs: 1, lg: 0 }}
        className={className}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          mb={1}
          mt={1}
          sx={{
            fontSize: { xs: "1.1rem", lg: "1.25rem" },
          }}
        >
          Upload Document Templates
        </Typography>
      </Box>

      <Box
        ref={containerRef}
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        gap={{ xs: 1, lg: 1 }}
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: {
            xs: "auto",
            sm: "auto",
            md: "calc(100vh - 240px)",
          },
          minHeight: {
            xs: "500px",
            sm: "600px",
            md: "600px",
          },
          maxHeight: {
            xs: "none",
            md: "calc(100vh - 200px)",
          },
          overflow: { xs: "visible", md: "hidden" },
          px: { xs: 1, lg: 2 },
          mb: { xs: 3, sm: 4, lg: 6 },
          pb: { xs: 2, lg: 0.2, sm: 1, md: 1 },
          pt: 0.5,
        }}
      >
        {/* Vertical Tabs */}
        <Box
          sx={{
            width: { xs: "60px", md: "60px" },
            minWidth: { xs: "60px", md: "60px" },
            maxWidth: { xs: "60px", md: "60px" },
            height: { xs: "auto", md: "100%" },
            minHeight: { xs: "650px", md: 0 },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            mb: { xs: 2, md: 0 },
            mr: { xs: 0, md: "-1px" },
            zIndex: 1,
          }}
        >
          <TabsList interactionTab={activeTab} handleTabChange={handleTabChange} />
        </Box>

        <TemplateListPanel
          list={manager.list}
          selectedIndex={manager.selectedIndex}
          activeTab={activeTab}
          templatesLoading={manager.templatesLoading}
          templateListCollapsed={templateListCollapsed}
          setTemplateListCollapsed={setTemplateListCollapsed}
          isGlobalAdmin={manager.isGlobalAdmin}
          onNewTemplate={manager.handleNewTemplate}
          onSelectTemplate={manager.handleTemplateSelect}
          onViewVersions={versionHistory.handleViewVersions}
          onCloneTemplate={manager.handleCloneTemplate}
          onDeleteRequest={manager.setPendingDeleteTemplateId}
          onToggleGlobalTemplate={manager.handleToggleGlobalTemplate}
        />

        {/* ─── CHANGED: Agreements tab renders AgreementsPage full-width;
                        all other tabs render the existing editor + preview ── */}
        {activeTab === 3 ? (
          // ── Agreements Tab ──────────────────────────────────────────────────
          <Box sx={{ flex: "1 1 auto", overflow: "auto", height: { xs: "auto", md: "100%" }, minHeight: { xs: "500px", md: 0 } }}>
            <AgreementsPage selectedOrganization={selectedOrganization} />
          </Box>
        ) : activeTab === 4 ? (
          // ── Fact Find New Tab ───────────────────────────────────────────────
          <Box sx={{ flex: "1 1 auto", overflow: "hidden", height: { xs: "auto", md: "100%" }, minHeight: { xs: "500px", md: 0 } }}>
            <FactFindOntologyPicker selectedOrganization={selectedOrganization} />
          </Box>
        ) : (
          // ── Existing Editor + Preview (tabs 0, 1, 2) — UNCHANGED ───────────
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", md: "row" },
              flex: { xs: "none", md: "1 1 auto" },
              width: {
                xs: "50%",
                md: templateListCollapsed
                  ? "calc(100% - 80px - 60px - 24px)"
                  : "calc(80% - 60px - 24px)",
              },
              gap: { xs: 1, lg: 1 },
              height: {
                xs: "50vh",
                md: "100%",
              },
              minHeight: {
                xs: "50vh",
                md: "100%",
              },
              boxSizing: "border-box",
            }}
          >
            <TemplateEditorPanel
              activeTab={activeTab}
              templateListCollapsed={templateListCollapsed}
              editorWidth={editorWidth}
              isDragging={isDragging}
              onResizeMouseDown={handleMouseDown}
              isReadOnlyMode={manager.isReadOnlyMode}
              importAnalyzing={manager.importAnalyzing}
              onOpenImportModal={() => manager.setImportModalOpen(true)}
              usesSectionEditor={manager.usesSectionEditor}
              editorMode={manager.editorMode}
              onEditorModeChange={manager.handleEditorModeChange}
              searchText={manager.searchText}
              setSearchText={manager.setSearchText}
              templateData={manager.templateData}
              setTemplateData={manager.setTemplateData}
              selectedOrganization={selectedOrganization}
              pptSwitch={manager.pptSwitch}
              onSetDefaultPpt={manager.handleSetDefaultPpt}
              selectedIndex={manager.selectedIndex}
              list={manager.list}
              powerPointFile={manager.powerPointFile}
              fileInputRef={manager.fileInputRef}
              onUploadBoxClick={manager.handleUploadBoxClick}
              onFileSelect={manager.handleFileSelect}
              templateSections={manager.templateSections}
              sectionsCollapseToken={manager.sectionsCollapseToken}
              onSectionsChange={manager.handleSectionsChange}
              monacoContent={manager.monacoContent}
              setMonacoContent={manager.setMonacoContent}
              onMonacoContentChange={manager.handleMonacoContentChange}
              onEditorMount={manager.handleEditorMount}
            />

            <TemplatePreviewPanel
              templateListCollapsed={templateListCollapsed}
              editorWidth={editorWidth}
              isDragging={isDragging}
              isReadOnlyMode={manager.isReadOnlyMode}
              templateData={manager.templateData}
              editorMarkdown={manager.editorMarkdown}
              previewHtml={manager.previewHtml}
              previewContainerRef={manager.previewContainerRef}
              isPpt={manager.isPpt}
              hasLocalPptFile={manager.hasLocalPptFile}
              isExistingPpt={manager.isExistingPpt}
              pptUrl={manager.pptUrl}
              powerPointFile={manager.powerPointFile}
              selectedIndex={manager.selectedIndex}
              list={manager.list}
              isGlobalAdmin={manager.isGlobalAdmin}
              loading={manager.loading}
              onSave={manager.handleUpload}
            />
          </Box>
        )}
        {/* ─── END CHANGED SECTION ─────────────────────────────────────────── */}
      </Box>

      {/* Version History Drawer */}
      <VersionHistoryDrawer
        open={versionHistory.versionDrawerOpen}
        onClose={versionHistory.handleCloseVersionDrawer}
        versions={versionHistory.versionHistory}
        currentVersionIdx={versionHistory.currentVersionIdx}
        selectedVersionIdxs={versionHistory.selectedVersionIdxs}
        onCheckboxChange={versionHistory.handleCheckboxChange}
        onRestoreVersion={versionHistory.handleRestoreVersion}
      />

      <ConfirmDeleteModal
        open={manager.pendingDeleteTemplateId !== null}
        entityName={
          manager.list.find((t) => t.templateId === manager.pendingDeleteTemplateId)
            ?.templateName ?? ""
        }
        onCancel={() => manager.setPendingDeleteTemplateId(null)}
        onConfirm={async () => {
          const id = manager.pendingDeleteTemplateId;
          if (!id) return;
          await manager.handleDelete(id);
          manager.setPendingDeleteTemplateId(null);
        }}
      />

      <ImportTemplateModal
        open={manager.importModalOpen}
        onClose={() => manager.setImportModalOpen(false)}
        onFileSelect={manager.handleImportFile}
        skillMode={activeTab === 6}
      />
    </>
  );
};

export default UploadDocumentTemplate;
